import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LanguageService } from '../i18n/language.service';
import {
  GeneralLandingSeoConfig,
  JsonLdThing,
  NoIndexConfig,
  PropertyManagerSeoConfig,
  SeoPageConfig,
  UnitSeoConfig,
} from './seo.models';

const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
];

const OG_LOCALE: Record<string, string> = {
  es: 'es_AR',
  en: 'en_US',
  pt: 'pt_BR',
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly language = inject(LanguageService);
  private readonly jsonLdId = 'guestgenie-jsonld';

  setGeneralLandingSEO(config: GeneralLandingSeoConfig): void {
    this.setPage({ ...config, openGraphType: 'website' });
  }

  setPropertyManagerSEO(config: PropertyManagerSeoConfig): void {
    this.setPage({ ...config, openGraphType: 'website' });
  }

  setUnitSEO(config: UnitSeoConfig): void {
    this.setPage({ ...config, openGraphType: 'website' });
  }

  setPage(config: SeoPageConfig): void {
    const canonical = config.canonicalUrl ?? this.currentCanonicalUrl();
    const image = config.image ? this.absoluteUrl(config.image) : undefined;
    const lang = this.language.activeLang();

    this.title.setTitle(config.title);
    this.document.documentElement.lang = lang;
    this.setNamedMeta('description', config.description);
    this.setNamedMeta('robots', config.robots ?? 'index, follow');
    this.setHttpEquivMeta('content-language', lang);

    this.setPropertyMeta('og:title', config.title);
    this.setPropertyMeta('og:description', config.description);
    this.setPropertyMeta('og:type', config.openGraphType ?? 'website');
    this.setPropertyMeta('og:url', canonical);
    this.setPropertyMeta('og:site_name', 'GuestGenie');
    this.setPropertyMeta('og:locale', OG_LOCALE[lang] ?? lang);
    this.setOptionalPropertyMeta('og:image', image);

    this.setNamedMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    this.setNamedMeta('twitter:title', config.title);
    this.setNamedMeta('twitter:description', config.description);
    this.setOptionalNamedMeta('twitter:image', image);

    this.setCanonical(canonical);
    this.setJsonLd(config.jsonLd);
  }

  setNoIndex(config: NoIndexConfig): void {
    const robots = config.follow === false ? 'noindex, nofollow' : 'noindex, follow';
    const lang = this.language.activeLang();

    this.title.setTitle(config.title);
    this.document.documentElement.lang = lang;
    this.setNamedMeta('robots', robots);
    this.setHttpEquivMeta('content-language', lang);
    this.removeNamedMeta('description');
    this.removeOpenGraphAndTwitter();
    this.setCanonical(config.canonicalUrl ?? this.currentCanonicalUrl());
    this.clearJsonLd();
  }

  setCanonical(url = this.currentCanonicalUrl()): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = this.absoluteUrl(url);
  }

  setJsonLd(data?: JsonLdThing | JsonLdThing[]): void {
    this.clearJsonLd();
    if (!data || (Array.isArray(data) && data.length === 0)) return;

    const script = this.document.createElement('script');
    script.id = this.jsonLdId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data).replace(/</g, '\\u003c');
    this.document.head.appendChild(script);
  }

  clearJsonLd(): void {
    this.document.getElementById(this.jsonLdId)?.remove();
  }

  currentCanonicalUrl(options: { dropQuery?: boolean } = {}): string {
    const base = this.document.defaultView?.location.origin ?? this.document.baseURI;
    const url = new URL(this.router.url || '/', base);
    url.hash = '';

    if (options.dropQuery) {
      url.search = '';
    } else {
      for (const param of TRACKING_PARAMS) url.searchParams.delete(param);
    }

    return url.toString();
  }

  absoluteUrl(value: string): string {
    const base = this.document.defaultView?.location.origin ?? this.document.baseURI;
    return new URL(value, base).toString();
  }

  private setNamedMeta(name: string, content: string): void {
    this.meta.updateTag({ name, content }, `name='${name}'`);
  }

  private setOptionalNamedMeta(name: string, content?: string): void {
    if (content) this.setNamedMeta(name, content);
    else this.removeNamedMeta(name);
  }

  private removeNamedMeta(name: string): void {
    this.meta.removeTag(`name='${name}'`);
  }

  private setPropertyMeta(property: string, content: string): void {
    this.meta.updateTag({ property, content }, `property='${property}'`);
  }

  private setOptionalPropertyMeta(property: string, content?: string): void {
    if (content) this.setPropertyMeta(property, content);
    else this.meta.removeTag(`property='${property}'`);
  }

  private setHttpEquivMeta(httpEquiv: string, content: string): void {
    this.meta.updateTag({ httpEquiv, content }, `http-equiv='${httpEquiv}'`);
  }

  private removeOpenGraphAndTwitter(): void {
    for (const tag of [...this.document.head.querySelectorAll<HTMLMetaElement>('meta')]) {
      const property = tag.getAttribute('property') ?? '';
      const name = tag.getAttribute('name') ?? '';
      if (property.startsWith('og:') || name.startsWith('twitter:')) tag.remove();
    }
  }
}
